import React, { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import {
    B2C_SITE_ORIGIN,
    MYFARM_SITE_ORIGIN,
} from "@danishagro/shared/src/constants/urlRoot.constants";
import { getRelativeRootOnLocalhost } from "@danishagro/shared/src/helpers/getRelativeUrlOnLocalhost.helper";
import { SiteHeaderPopUpType, useSiteHeader } from "../../../../contexts/siteHeader.context";
import { useScreen } from "../../../../contexts/screen.context";
import { DA_Title, DA_TitleTag } from "../../../atoms/Title/Title.component";
import { DA_BasicLink } from "../../../atoms/BasicLink/BasicLink.component";
import { DA_Icon, DA_IconNames } from "../../../atoms/Icon/Icon.component";
import { useTranslations } from "../../../../contexts/translations/translations.context";
import { useBottomSheet } from "../../../../contexts/bottomSheet/bottomSheet.context";
import { DA_Spinner } from "../../../atoms/Spinner/Spinner.component";
import { useAppData } from "../../../../contexts/appData.context";
import { DA_AccountPickerPopUpProps, DA_AccountPickerProps } from "./AccountPicker.props";
import S from "./AccountPicker.module.scss";

export const DA_AccountPicker = (_props: DA_AccountPickerProps) => {
    const [isChangingCustomer, setIsChangingCustomer] = useState(false);
    const { headerMeasure, accountButtonMeasure, togglePopUp } = useSiteHeader();
    const { isMobileOrTablet } = useScreen();
    const { dismissBottomSheet } = useBottomSheet();
    const { getDictionaryString } = useTranslations();
    const {
        isAdmin,
        isMasterAdmin,
        isImpersonator,
        cvrAndCustomerNumbers,
        customerNumber,
        changeCustomer,
        currentSite,
    } = useAppData();

    const menuRef = useRef<HTMLDivElement>(null);
    const triggerButtonRef = useRef<HTMLButtonElement>(null);

    const changeAccount = useCallback(
        (accountNumber: string) => {
            setIsChangingCustomer(true);

            // Optionally, guard against re-clicking the same account:
            if (accountNumber === customerNumber) {
                // user clicked the same account, so just close and do nothing else
                togglePopUp(SiteHeaderPopUpType.AccountPicker);
                dismissBottomSheet();
                setIsChangingCustomer(false);
                return;
            }

            changeCustomer(accountNumber, true)
                .then(() => {
                    // Close any pop-ups
                    togglePopUp(SiteHeaderPopUpType.AccountPicker);
                    dismissBottomSheet();

                    // **Now** force reload (only on user action, so no infinite loop)
                    window.location.reload();
                })
                .catch((err) => {
                    // handle or log err if needed
                    console.error(err);
                })
                .finally(() => {
                    setIsChangingCustomer(false);
                });
        },
        [changeCustomer, customerNumber, dismissBottomSheet, togglePopUp]
    );

    const onCloseButtonClicked = () => {
        dismissBottomSheet();
        togglePopUp(undefined);
        triggerButtonRef.current?.focus(); // Return focus to the trigger button
    };

    const getLinkUrl = useCallback(
        (path: string) => {
            if (currentSite === "MYFARM") {
                return path;
            } else if (currentSite === "B2C") {
                return getRelativeRootOnLocalhost(B2C_SITE_ORIGIN) + path;
            }
            return MYFARM_SITE_ORIGIN + path;
        },
        [currentSite]
    );

    // Trap focus within the menu
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                togglePopUp(undefined);
                requestAnimationFrame(() => {
                    triggerButtonRef.current?.focus();
                });
            } else if (event.key === "Tab" && menuRef.current) {
                const focusableElements = menuRef.current.querySelectorAll<HTMLElement>(
                    "a, button, input, [tabindex]:not([tabindex='-1'])"
                );
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                if (event.shiftKey && document.activeElement === firstElement) {
                    // Shift + Tab: Move focus to the last element
                    event.preventDefault();
                    lastElement.focus();
                } else if (!event.shiftKey && document.activeElement === lastElement) {
                    // Tab: Move focus to the first element
                    event.preventDefault();
                    firstElement.focus();
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [togglePopUp]);

    requestAnimationFrame(() => {
        triggerButtonRef.current?.focus();
    });

    useEffect(() => {
        if (menuRef.current) {
            const focusableElements = menuRef.current.querySelectorAll<HTMLElement>(
                "a, button, input, [tabindex]:not([tabindex='-1'])"
            );
            const firstElement = focusableElements[0];
            firstElement?.focus();
        }
    }, []); // Runs once when component mounts (i.e., menu opens)

    return (
        <div
            ref={menuRef}
            className={cn(
                S.inside,
                cvrAndCustomerNumbers?.accounts?.length > 1 && S.hasMultipleCompanies,
                cvrAndCustomerNumbers?.accounts?.length > 2 && S.hasThreeOrMoreCompanies,
                _props.accountPickerB2C && S.insideB2c
            )}
            style={{
                maxHeight:
                    !isMobileOrTablet && accountButtonMeasure
                        ? `calc(100vh - ${
                              accountButtonMeasure.top + accountButtonMeasure.bottom
                          }px)`
                        : undefined,
                right: !isMobileOrTablet && headerMeasure ? `${headerMeasure.left}px` : undefined,
                top:
                    !isMobileOrTablet && accountButtonMeasure
                        ? `${accountButtonMeasure.top + accountButtonMeasure.bottom}px`
                        : undefined,
            }}
        >
            <div className={cn(S.top, _props.accountPickerB2C && S.topB2c)}>
                {(!isMobileOrTablet || currentSite !== "B2C") && (
                    <div className={S.userName}>
                        {cvrAndCustomerNumbers.firstName} {cvrAndCustomerNumbers.lastName}
                    </div>
                )}

                <ul
                    className={cn(S.buttons, _props.accountPickerB2C && S.buttonsB2c)}
                    role="menubar"
                >
                    {/** Button: Administrator */}
                    {(isAdmin ||
                        isMasterAdmin ||
                        (!isAdmin && !isMasterAdmin && isImpersonator)) && (
                        <li
                            role="menuitem"
                            className={cn(
                                S.buttonWrapper,
                                _props.accountPickerB2C && S.buttonWrapperB2c
                            )}
                        >
                            <DA_BasicLink
                                href={getLinkUrl("/admin")}
                                target="_self"
                                className={S.buttonLink}
                                onClick={onCloseButtonClicked}
                            >
                                <DA_Icon name={DA_IconNames.UserCrown} className={S.buttonIcon} />
                                <span className={S.buttonText}>
                                    {_props.accountPickerB2C
                                        ? getDictionaryString("header.profileMenu.administrator", {
                                              uppercaseFirst: true,
                                          })
                                        : getDictionaryString("administrator", {
                                              uppercaseFirst: true,
                                          })}
                                </span>
                            </DA_BasicLink>
                        </li>
                    )}

                    {/** Button: Notifications */}
                    {/* NOTE: Removed for now because notifications isn't ready for launch. */}
                    {/* <div
                        className={cn(
                            S.buttonWrapper,
                            _props.accountPickerB2C && S.buttonWrapperB2c
                        )}
                    >
                        <DA_BasicLink
                            href={getLinkUrl("/notifications")}
                            target="_self"
                            className={S.buttonLink}
                            onClick={onCloseButtonClicked}
                        >
                            <div className={S.buttonIconWrapper}>
                                <DA_Icon name={DA_IconNames.Bell} className={S.buttonIcon} />

                                {props.notificationCount > 0 ? (
                                    <DA_Badge
                                        size="small"
                                        text={props.notificationCount}
                                        className={S.buttonIconBadge}
                                    />
                                ) : null}
                            </div>

                            <span className={S.buttonText}>
                                {getDictionaryString("notificationsTitle", {
                                    uppercaseFirst: true,
                                })}
                            </span>
                        </DA_BasicLink>
                    </div> */}

                    {/** Button: Settings/Profile */}
                    <li
                        role="menuitem"
                        className={cn(
                            S.buttonWrapper,
                            _props.accountPickerB2C && S.buttonWrapperB2c
                        )}
                    >
                        <DA_BasicLink
                            href={getLinkUrl("/profile")}
                            target="_self"
                            className={S.buttonLink}
                            onClick={onCloseButtonClicked}
                        >
                            <DA_Icon
                                name={
                                    _props.accountPickerB2C
                                        ? DA_IconNames.UserLoggedOut
                                        : DA_IconNames.CogwheelStroke
                                }
                                className={S.buttonIcon}
                            />
                            <span className={S.buttonText}>
                                {_props.accountPickerB2C
                                    ? getDictionaryString("header.profileMenu.myAccount", {
                                          uppercaseFirst: true,
                                      })
                                    : getDictionaryString("settings", {
                                          uppercaseFirst: true,
                                      })}
                            </span>
                        </DA_BasicLink>
                    </li>

                    {/** Button: Order Overview */}
                    {_props.accountPickerB2C && (
                        <li
                            role="menuitem"
                            className={cn(
                                S.buttonWrapper,
                                _props.accountPickerB2C && S.buttonWrapperB2c
                            )}
                        >
                            <DA_BasicLink
                                href={getLinkUrl(
                                    `/profile#tab-${getDictionaryString(
                                        "header.profileMenu.orderOverview"
                                    )}`
                                )}
                                target="_self"
                                className={S.buttonLink}
                                onClick={onCloseButtonClicked}
                            >
                                <DA_Icon name={DA_IconNames.Clipboard} className={S.buttonIcon} />
                                <span className={S.buttonText}>
                                    {getDictionaryString("header.profileMenu.orderOverview", {
                                        uppercaseFirst: true,
                                    })}
                                </span>
                            </DA_BasicLink>
                        </li>
                    )}

                    {/** Button: Logout */}
                    <li
                        role="menuitem"
                        className={cn(
                            S.buttonWrapper,
                            _props.accountPickerB2C && S.buttonWrapperB2c
                        )}
                    >
                        <DA_BasicLink
                            // All sites should have their own logout page
                            // that sends you to the front page when logged out
                            href="/logout"
                            target="_self"
                            conventionalRouting
                            className={S.buttonLink}
                            onClick={() => {
                                onCloseButtonClicked();
                            }}
                        >
                            <DA_Icon name={DA_IconNames.SignOut} className={S.buttonIcon} />
                            <span className={S.buttonText}>
                                {_props.accountPickerB2C
                                    ? getDictionaryString("header.profileMenu.logOut", {
                                          uppercaseFirst: true,
                                      })
                                    : getDictionaryString("logOut", {
                                          uppercaseFirst: true,
                                      })}
                            </span>
                        </DA_BasicLink>
                    </li>
                </ul>
            </div>
            {cvrAndCustomerNumbers?.accounts && (
                <div className={S.companies}>
                    {cvrAndCustomerNumbers?.accounts?.map((account, accountIndex) => (
                        <section key={`${account.cvr}-${accountIndex}`} className={S.company}>
                            <div className={S.vatNumber}>CVR {account.cvr}</div>
                            <DA_Title h3 tag={DA_TitleTag.DIV}>
                                {account.name}
                            </DA_Title>
                            <ul className={S.accounts}>
                                {account.customers.map((customer) => (
                                    <li
                                        key={`${account.cvr}-${accountIndex}-${customer.number}`}
                                        className={S.account}
                                    >
                                        <button
                                            type="button"
                                            className={cn(
                                                S.accountButton,
                                                customer.number === customerNumber && S.active
                                            )}
                                            onClick={() => changeAccount(String(customer.number))}
                                        >
                                            <span className={S.accountName}>{customer.name}</span>
                                            <span className={S.accountNumber}>
                                                <span className={S.accountLabel}>
                                                    {getDictionaryString("account", {
                                                        uppercaseFirst: true,
                                                    })}
                                                </span>
                                                {customer.number}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>
            )}

            {isChangingCustomer && (
                <div className={S.changingCustomerOverlay}>
                    <div>
                        <DA_Spinner />
                        <div className={S.text}>
                            {getDictionaryString("Changing customer number")}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export const DA_AccountPickerPopUp = (props: DA_AccountPickerPopUpProps) => {
    const { headerMeasure, accountButtonMeasure } = useSiteHeader();
    const { isMobileOrTablet } = useScreen();
    const buttonPadding = 10;

    const rightPos = props.accountPickerB2C
        ? accountButtonMeasure.right + buttonPadding
        : headerMeasure.left;

    // NOTE: Removed for now because notifications isn't ready for launch.
    // const { notificationCount } = useNotifications();
    const navRef = useRef<HTMLDivElement>();

    return (
        <div
            role="menu"
            ref={navRef}
            className={S.wrapper}
            style={{
                right: isMobileOrTablet ? undefined : `${rightPos}px`,
                top: !isMobileOrTablet && accountButtonMeasure ? 0 : undefined,
            }}
        >
            <DA_AccountPicker
                accountPickerB2C={props.accountPickerB2C}
                notificationCount={undefined}
            />
        </div>
    );
};
